import { Platform } from '@angular/cdk/platform';
import { Component, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationStart,
  Router,
  Event as RouterEvent,
} from '@angular/router';
import { setTheme } from 'ngx-bootstrap/utils';
import { Subscription } from 'rxjs';
import * as URI from 'urijs';

import { AppConfig } from '®shared/config/app-config';
import { ensureDateLocale } from '®shared/dates/date-utils';
import { ProductFruitsService } from '®shared/product-fruits';

const RESOLVED_MODE_BASED_LIST_REGEX =
  /\/3\/([^\/]+)\/lists\/([^\/]+)\/modes\/.*$/;
const UNRESOLVED_MODE_BASED_LIST_REGEX =
  /\/3\/([^\/]+)\/lists\/([^\/]+)(\/edit)?$/;
const RESOLVED_LIST_CREATE_ROUTE_REGEX = /\/3\/([^\/]+)\/lists\/create\.html$/;

@Component({
  selector: 'rl-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnDestroy, OnInit {
  appName = 'shipshape';
  loading = true;
  isNavbarVisible = false;
  ltiRelinkUrl = this.appConfig.ltiRelinkUrl;
  isMobile = this.platform.IOS || this.platform.ANDROID;

  private routerEventSubscription: Subscription;

  ngOnDestroy() {
    if (this.routerEventSubscription) {
      this.routerEventSubscription.unsubscribe();
    }
  }

  constructor(
    private appConfig: AppConfig,
    public platform: Platform,
    public router: Router,
    private productFruitsService: ProductFruitsService,
    @Inject(LOCALE_ID) public locale: string,
  ) {
    setTheme('bs5');
  }

  ngOnInit() {
    this.productFruitsService.init();

    this.routerEventSubscription = this.router.events.subscribe(
      (event: RouterEvent) => {
        this.navigationInterceptor(event);
      },
    );

    ensureDateLocale(this.locale);
  }

  /**
   * Is this page view in embedded mode, and not a list?
   * Used to hide the navbar on LTI sections (non-lists)
   *
   * @param  {string}  url The current page URL
   * @return {boolean}     `true` when embedded and not a list view; `false` otherwise
   */
  shouldNavbarBeHidden(url: string): boolean {
    const uri = new URI(url);

    // always show the navbar at the new list screen
    if (uri.path().match(RESOLVED_LIST_CREATE_ROUTE_REGEX)) {
      return false;
    }

    // The mode-based router should hide the navbar until the content fully resolves.
    // We do this to prevent flash-of-content effects of loading navbar first, then content
    if (uri.path().match(UNRESOLVED_MODE_BASED_LIST_REGEX)) {
      return true;
    }

    // the error pages always hide the navbar
    if (uri.path().match(/^\/error(.html)?$/)) {
      return true;
    }

    // we only hide the navbar when iframed (and never when directly accessed)
    if (this.appConfig.isAnLti1p0EmbeddedSession !== true) {
      return false;
    }

    // we never hide the navbar for lists, even when iframed
    if (uri.path().match(RESOLVED_MODE_BASED_LIST_REGEX)) {
      return false;
    }

    // all other resolved content (sections, items) should hide the navbar
    return true;
  }

  /**
   * Show the navbar when not embedded, or viewing a list
   * @param {string} url The current page URL
   */
  configureNavbarVisibility(url: string): void {
    this.isNavbarVisible = !this.shouldNavbarBeHidden(url);
  }

  navigationInterceptor(event: RouterEvent) {
    if (event instanceof NavigationStart) {
      this.loading = true;
    }

    if (event instanceof NavigationCancel) {
      this.loading = false;
    }

    if (event instanceof NavigationEnd) {
      this.loading = false;
      this.configureNavbarVisibility(event.urlAfterRedirects);
    }
  }
}
